import { useState } from 'react'

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { toast } from 'react-hot-toast'
import type { UserQuery } from 'types/graphql'

import { useAuth } from 'src/auth'
import {
  Button,
  Divider,
  Icon,
  IconButton,
  Popover,
  Switch,
  Text,
  UserIcon,
} from 'src/design-system/ui'
import { navigate } from 'src/platform/router'

import './evie-top-bar.css'

export const EVIE_ACCOUNT_QUERY = gql`
  query UserQuery {
    currentUser {
      id
      email
      roles
    }
  }
`

/** Pre-chat applicant chrome. Chat keeps its existing in-flow running head. */
export const EvieTopBar = () => {
  const { isAuthenticated, logOut } = useAuth()
  const { data } = useQuery<UserQuery>(EVIE_ACCOUNT_QUERY, {
    skip: !isAuthenticated,
  })
  const [open, setOpen] = useState(false)
  const [businessUser, setBusinessUser] = useState(() => {
    try {
      return localStorage.getItem('isBusinessUser') === 'true'
    } catch {
      return false
    }
  })
  const user = data?.currentUser
  const roles = Array.isArray(user?.roles)
    ? user.roles.filter((role): role is string => typeof role === 'string')
    : []
  const logout = async () => {
    setOpen(false)
    try {
      await logOut({ postLogoutRedirectUri: window.location.origin })
      navigate('/')
    } catch {
      toast.error('Unable to logout.')
    }
  }
  return (
    <header className="evie-top-bar">
      <img src="/Two Dots_Wordmark_Black.svg" alt="Two Dots" />
      {isAuthenticated && (
        <div className="ds:ml-auto ds:flex ds:items-center ds:gap-3">
          <Button
            variant="secondary"
            size="compact"
            leadingIcon={<Icon name="support" size="sm" tone="current" />}
            onPress={() => window.Intercom('show')}
          >
            Support
          </Button>
          {user && (
            <Popover
              open={open}
              onOpenChange={setOpen}
              ariaLabel="Account"
              placement="bottom end"
              trigger={
                <IconButton aria-label="Account" variant="quiet">
                  <Icon icon={UserIcon} />
                </IconButton>
              }
            >
              <div className="ds:grid ds:gap-3 ds:p-3">
                <Text variant="body-strong">{user.email}</Text>
                {roles.length > 0 && (
                  <Text variant="caption" tone="muted">
                    Roles: {roles.join(', ')}
                  </Text>
                )}
                {roles.includes('Super') && (
                  <Switch
                    isSelected={businessUser}
                    onChange={(value) => {
                      setBusinessUser(value)
                      try {
                        if (value)
                          localStorage.setItem('isBusinessUser', 'true')
                        else localStorage.removeItem('isBusinessUser')
                      } catch {
                        /* Keep the local selection usable. */
                      }
                    }}
                  >
                    Business user view
                  </Switch>
                )}
                {roles.some((role) =>
                  ['Admin', 'Accountant'].includes(role)
                ) && (
                  <Button
                    variant="quiet"
                    onPress={() => {
                      setOpen(false)
                      navigate('/nonqm-accounting')
                    }}
                  >
                    Mortgage accounting
                  </Button>
                )}
                <Divider spacing="none" />
                <Button variant="quiet" onPress={() => void logout()}>
                  Logout
                </Button>
              </div>
            </Popover>
          )}
        </div>
      )}
    </header>
  )
}
